import { useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { JsonView } from "@/components/json-view";
import { Panel } from "@/components/panel";
import { QueryErrorState } from "@/components/query-error-state";
import { StatCard } from "@/components/stat-card";
import { Select } from "@/components/ui/select";
import { apiGet } from "@/lib/api-client";
import { asRecord, asArray } from "@/lib/type-utils";

const INDICATOR_OPTIONS = [
  { id: "unemployment", name: "Unemployment Rate", series: "UNRATE" },
  { id: "inflation", name: "CPI Inflation", series: "CPIAUCSL" },
  { id: "gdp", name: "GDP Growth", series: "GDP" },
  { id: "interest", name: "Fed Funds Rate", series: "FEDFUNDS" },
  { id: "jobs", name: "Non-Farm Payrolls", series: "PAYEMS" },
  { id: "recession", name: "Recession Probability", series: "RECPROUSM156N" },
];

export function FredEconomicPage() {
  const [selectedIndicator, setSelectedIndicator] = useState("unemployment");

  const selectedSeries = INDICATOR_OPTIONS.find((o) => o.id === selectedIndicator)?.series ?? "UNRATE";

  const seriesQuery = useQuery({
    queryKey: ["fred", "series", selectedSeries],
    queryFn: () => apiGet<unknown>(`/api/fred/series/${selectedSeries}`),
    refetchInterval: 600_000,
  });

  const snapshotQuery = useQuery({
    queryKey: ["fred", "snapshot"],
    queryFn: () => apiGet<unknown>("/api/fred/snapshot"),
    refetchInterval: 600_000,
  });

  const series = asRecord(seriesQuery.data);
  // FRED returns the native payload: an "observations" array, newest first
  // (sort_order=desc in backend/services/fred.py). "." is FRED's sentinel for
  // a missing observation — drop those before parsing numbers.
  const observations = asArray(series.observations).filter((obs) => obs.value !== ".");

  const currentObs = observations[0];
  const previousObs = observations[1];
  const currentValue = currentObs ? Number(currentObs.value) : null;
  const previousValue = previousObs ? Number(previousObs.value) : null;
  const change = currentValue !== null && previousValue !== null ? currentValue - previousValue : null;
  const changePct = change !== null && previousValue !== null && previousValue !== 0 ? (change / previousValue) * 100 : null;

  const snapshot = asRecord(snapshotQuery.data);
  const snapshotIndicators = asRecord(snapshot.indicators);

  const indicatorOptions = INDICATOR_OPTIONS;
  const selectedName = indicatorOptions.find((o) => o.id === selectedIndicator)?.name ?? "";

  return (
    <div className="space-y-4 md:space-y-6">
      <Panel
        title="FRED Economic Data"
        description="Federal Reserve Economic Data for market correlation analysis and macro trends."
      >
        {seriesQuery.isError ? (
          <QueryErrorState what="FRED series data" onRetry={() => void seriesQuery.refetch()} />
        ) : (
          <div className="grid gap-2 md:gap-3 grid-cols-2 md:grid-cols-[repeat(auto-fit,minmax(11rem,1fr))]">
            <StatCard
              label="Current Value"
              value={currentValue !== null ? currentValue.toFixed(2) : "—"}
              hint={selectedName}
            />
            <StatCard
              label="Change"
              value={change !== null ? `${change >= 0 ? "+" : ""}${change.toFixed(2)}` : "—"}
              tone={change !== null && change > 0 ? "success" : change !== null && change < 0 ? "warning" : "default"}
              hint="Period over period"
            />
            <StatCard
              label="Change %"
              value={changePct !== null ? `${changePct >= 0 ? "+" : ""}${changePct.toFixed(2)}%` : "—"}
              tone={changePct !== null && changePct > 0 ? "success" : changePct !== null && changePct < 0 ? "warning" : "default"}
            />
            <StatCard
              label="Last Updated"
              value={String(currentObs?.date ?? "—")}
            />
          </div>
        )}
      </Panel>

      <Panel
        title="Economic Indicators"
        description="Select an indicator to view historical data"
        actions={
          <Select
            className="text-sm"
            value={selectedIndicator}
            onChange={(e) => setSelectedIndicator(e.target.value)}
          >
            {indicatorOptions.map((opt) => (
              <option key={opt.id} value={opt.id}>
                {opt.name}
              </option>
            ))}
          </Select>
        }
      >
        {seriesQuery.isError ? (
          <QueryErrorState what="indicator history" onRetry={() => void seriesQuery.refetch()} />
        ) : (
          <div className="rounded-lg bg-gunmetal/50 p-4">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-gold-400">
                  {currentValue !== null ? currentValue.toFixed(2) : "—"}
                </div>
                <div className="text-xs text-slate-400">{selectedName}</div>
              </div>
              {changePct !== null ? (
                <div className={`text-sm ${changePct >= 0 ? "text-green-400" : "text-red-400"}`}>
                  {changePct >= 0 ? "↑" : "↓"} {Math.abs(changePct).toFixed(2)}%
                </div>
              ) : null}
            </div>

            <div className="space-y-2">
              <div className="flex justify-between text-xs text-slate-400">
                <span>Historical Data (newest first)</span>
                <span>{observations.length} data points</span>
              </div>
              <div className="max-h-[200px] space-y-1 overflow-y-auto">
                {observations.length === 0 ? (
                  <div className="text-center text-sm text-slate-400">No historical data available</div>
                ) : (
                  observations.slice(0, 20).map((point, index) => (
                    <div key={index} className="flex justify-between text-sm">
                      <span className="text-slate-400">{String(point.date ?? "-")}</span>
                      <span className="font-medium">{Number(point.value).toFixed(2)}</span>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}
      </Panel>

      <div className="grid gap-3 md:gap-4 grid-cols-1 lg:grid-cols-2">
        <Panel title="Economic Snapshot" description="Latest observation per key indicator">
          <div className="max-h-[300px] space-y-2 overflow-y-auto">
            {snapshotQuery.isError ? (
              <QueryErrorState what="economic snapshot" onRetry={() => void snapshotQuery.refetch()} />
            ) : Object.keys(snapshotIndicators).length === 0 ? (
              <div className="rounded-lg bg-gunmetal/50 p-4 text-center text-sm text-slate-400">
                No snapshot data available
              </div>
            ) : (
              Object.entries(snapshotIndicators).map(([key, value]) => {
                const indicator = asRecord(value);
                const isMissing = indicator.value === "." || indicator.value == null;
                return (
                  <div key={key} className="flex items-center justify-between rounded-lg bg-gunmetal/50 p-3">
                    <div>
                      <div className="text-sm font-medium">{key}</div>
                      <div className="text-xs text-slate-400">{String(indicator.series_id ?? "")}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-gold-400">
                        {isMissing ? "—" : Number(indicator.value).toFixed(2)}
                      </div>
                      <div className="text-xs text-slate-400">{String(indicator.date ?? "—")}</div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </Panel>

        <Panel title="Indicator Summary" description="Key economic indicators at a glance">
          {snapshotQuery.isError ? (
            <QueryErrorState what="indicator summary" onRetry={() => void snapshotQuery.refetch()} />
          ) : (
            <div className="grid grid-cols-2 gap-3">
              {Object.entries(snapshotIndicators).map(([key, value]) => {
                const indicator = asRecord(value);
                const isMissing = indicator.value === "." || indicator.value == null;
                return (
                  <div key={key} className="rounded-lg bg-gunmetal/50 p-3 text-center">
                    <div className="text-lg font-bold text-gold-400">
                      {isMissing ? "—" : Number(indicator.value).toFixed(2)}
                    </div>
                    <div className="text-xs text-slate-400">{key}</div>
                  </div>
                );
              })}
              {Object.keys(snapshotIndicators).length === 0 && (
                <div className="col-span-2 rounded-lg bg-gunmetal/50 p-4 text-center text-sm text-slate-400">
                  No indicator data available
                </div>
              )}
            </div>
          )}
        </Panel>
      </div>

      <div className="grid gap-3 md:gap-4 grid-cols-1 lg:grid-cols-2">
        <Panel title="Series API" description="Raw FRED series data">
          <JsonView value={seriesQuery.data} />
        </Panel>
        <Panel title="Snapshot API" description="Raw economic snapshot data">
          <JsonView value={snapshotQuery.data} />
        </Panel>
      </div>
    </div>
  );
}
